import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop handles two distinct navigation behaviors:
 * 1. Standard page navigation (no hash): Immediately scrolls window to top (0, 0)
 *    so the user lands at the top of the destination page.
 * 2. Anchor navigation (e.g. #pricing): Animates a smooth scroll to the target
 *    anchor element, whether navigating from another page or scrolling in-page
 *    (up or down relative to current scroll position).
 */
function safeScrollTo(options: ScrollToOptions) {
  if (typeof window !== 'undefined') {
    if (typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(options);
      } catch {
        // Ignore jsdom or environment restrictions
      }
    }
    try {
      if (document.documentElement && typeof document.documentElement.scrollTo === 'function') {
        document.documentElement.scrollTo(options);
      }
      if (document.body && typeof document.body.scrollTo === 'function') {
        document.body.scrollTo(options);
      }
      const scrollableContainers = document.querySelectorAll<HTMLElement>(
        'main, [data-scroll-container], #main-content'
      );
      scrollableContainers.forEach((el) => {
        if (typeof el.scrollTo === 'function') {
          el.scrollTo(options);
        }
        el.scrollTop = 0;
        el.scrollLeft = 0;
      });
    } catch {
      // Ignore environment restrictions
    }
  }
}

function safeScrollIntoView(element: HTMLElement) {
  try {
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (typeof window !== 'undefined') {
      const rect = element.getBoundingClientRect();
      safeScrollTo({
        top: Math.max(0, rect.top + (window.pageYOffset || 0) - 80),
        behavior: 'smooth',
      });
    }
  } catch {
    // Ignore environment restrictions
  }
}

export function ScrollToTop() {
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const isNewPage = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    // Case 1: Standard navigation without hash -> Instant scroll to top
    if (!location.hash) {
      safeScrollTo({ top: 0, left: 0, behavior: 'instant' });
      const rafId = requestAnimationFrame(() => {
        safeScrollTo({ top: 0, left: 0, behavior: 'instant' });
      });
      return () => cancelAnimationFrame(rafId);
    }

    // Case 2: Navigation with an anchor hash (e.g. #pricing)
    const elementId = location.hash.replace(/^#/, '');
    if (!elementId || elementId === 'main-content') {
      return;
    }

    // If navigating to a new page with a hash, first ensure the page starts at top
    // so the smooth scroll animates clearly to the anchor
    if (isNewPage) {
      safeScrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    let isMounted = true;
    let attempts = 0;
    const maxAttempts = 30; // ~1.5s total at 50ms intervals for lazy-loaded components

    const scrollToTarget = () => {
      if (!isMounted) return true;
      const target = document.getElementById(elementId);
      if (target) {
        // Delay to next animation frame so layout has settled before scrolling
        requestAnimationFrame(() => {
          if (!isMounted) return;
          safeScrollIntoView(target);
        });
        return true;
      }
      return false;
    };

    if (!scrollToTarget()) {
      const interval = setInterval(() => {
        attempts++;
        if (scrollToTarget() || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 50);

      return () => {
        isMounted = false;
        clearInterval(interval);
      };
    }

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.hash]);

  // Global click interceptor for in-page anchors to guarantee smooth animated scroll
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest?.('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Match #anchor or /#anchor when on "/"
      const isCurrentPageAnchor =
        href.startsWith('#') ||
        (href.startsWith('/#') && window.location.pathname === '/');

      if (!isCurrentPageAnchor) return;

      const hash = href.startsWith('/#') ? href.slice(2) : href.slice(1);
      if (!hash || hash === 'main-content') return; // let accessibility skip links function natively

      const target = document.getElementById(hash);
      if (target) {
        e.preventDefault();
        safeScrollIntoView(target);
        window.history.pushState(null, '', `#${hash}`);
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, []);

  return null;
}
